/**
 * The windows the app offers, and the layout used when the config has none (or before it
 * arrives). Adding a window: write its component under `src/windows/`, list it here, and add
 * its name to `WINDOWS` in `modules/mod-config/src/schema/mod.rs` so the config accepts it.
 */
import { AddNodeWindow } from "../windows/add-node/AddNodeWindow";
import { AiWindow } from "../windows/ai/AiWindow";
import { AnalyzeWindow } from "../windows/analyze/AnalyzeWindow";
import { ConfigWindow } from "../windows/config/ConfigWindow";
import { FiltersWindow } from "../windows/filters/FiltersWindow";
import { GraphWindow } from "../windows/graph/GraphWindow";
import { InspectorWindow } from "../windows/inspector/InspectorWindow";
import { LogWindow } from "../windows/log/LogWindow";
import { PluginsWindow } from "../windows/plugins/PluginsWindow";
import { PresetsWindow } from "../windows/presets/PresetsWindow";
import { ProjectWindow } from "../windows/project/ProjectWindow";
import { ShareWindow } from "../windows/share/ShareWindow";
import { HomeWindow } from "../windows/home/HomeWindow";
import { PageWindow } from "../windows/page/PageWindow";
import { PagesWindow } from "../windows/pages/PagesWindow";
import { PlayWindow } from "../windows/play/PlayWindow";
import { PrototypeWindow } from "../windows/prototype/PrototypeWindow";
import { split, tabs, type Layout } from "../wm/layout";
import type { WindowDef } from "../wm/manager";

/** Every window, in the order of the Window menu (by `group`). */
export const WINDOWS: WindowDef[] = [
  { id: "graph", title: "Graph", group: "Graph", component: GraphWindow },
  { id: "filters", title: "Filters", group: "Graph", component: FiltersWindow },
  { id: "inspector", title: "Inspector", group: "Graph", component: InspectorWindow },
  // Not in the built-in layout: opened by the environments that list them (Writing, UI/UX...).
  { id: "page", title: "Page", group: "Graph", component: PageWindow, byEnvironment: { beside: "filters", zone: "center" } },
  { id: "pages", title: "Pages", group: "Graph", component: PagesWindow, byEnvironment: { beside: "graph", zone: "left", share: 0.25 } },
  { id: "play", title: "Play", group: "Graph", component: PlayWindow, byEnvironment: { beside: "page", zone: "center" } },
  { id: "prototype", title: "Prototype", group: "Graph", component: PrototypeWindow },
  { id: "presets", title: "Assets", group: "Project", component: PresetsWindow },
  { id: "project", title: "Project", group: "Project", component: ProjectWindow },
  { id: "analyze", title: "Analyze", group: "Project", component: AnalyzeWindow },
  { id: "ai", title: "AI", group: "Project", component: AiWindow },
  { id: "plugins", title: "Plugins", group: "App", component: PluginsWindow },
  { id: "log", title: "Log", group: "App", component: LogWindow },
  { id: "config", title: "Config", group: "App", component: ConfigWindow },
  // Not in the built-in layout: opened from the menu bar's Share button (spaces only).
  { id: "share", title: "Share", group: "Together", component: ShareWindow },
  { id: "home", title: "Spaces", group: "Together", component: HomeWindow },
  // Not in the built-in layout: floated beside a right click on the graph ("Add a node…"), or key N.
  { id: "add-node", title: "Add node", group: "Graph", component: AddNodeWindow },
];

/**
 * The built-in layout, the same as `config/default.kdl`: one main view and one side panel (the
 * inspector over the filters). The other windows open on demand (Window menu, toolbar, Ctrl+K),
 * as tabs beside the filters, so the inspector stays in sight.
 */
export function builtInLayout(): Layout {
  return {
    root: split(
      "row",
      [tabs(["graph"]), split("column", [tabs(["inspector"]), tabs(["filters"])], [0.62, 0.38])],
      [0.74, 0.26],
    ),
    floating: [],
    closed: [],
  };
}
